/**
 * UTM Modal Custom Element
 *
 * Displays a modal when a matching UTM parameter is detected in the URL.
 * Uses native HTML <dialog> element for built-in modal functionality.
 * Shows modal only once per session when matching UTM parameters are detected.
**/

class UTMModal extends HTMLElement {
  constructor() {
    super();
    this.sessionKey = `utm_modal_shown_${this.id}`;
    this.dialog = this.querySelector('dialog');
  }

  connectedCallback() {
    if (!this.dialog) {
      console.error('UTM Modal: No <dialog> element found');
      return;
    }

    // Set up close button
    const closeButton = this.querySelector('[id^="ModalClose-"]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    // Set up CTA close button if it exists (button with no URL)
    const ctaCloseButton = this.querySelector('[id^="ModalCloseCTA-"]');
    if (ctaCloseButton) {
      ctaCloseButton.addEventListener('click', () => this.hide());
    }

    // Close on backdrop click
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) {
        this.hide();
      }
    });

    // Close on ESC key (native behavior, but we need to handle our cleanup)
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault(); // Prevent default so we can run our hide() method
      this.hide();
    });

    // Initialize UTM-specific logic
    this.initUTM();
  }

  initUTM() {
    // Check if already shown this session
    if (sessionStorage.getItem(this.sessionKey) && this.dataset.debugMode != 'true') {
      this.dispatchPriorityResolved('already-shown');
      return;
    }

    // Check if URL has matching UTM parameter and show if match found
    if (this.hasMatchingUTMParameter()) {
      this.triggerModal();
    } else {
      // No matching UTM parameters - dispatch event so other modals can proceed
      this.dispatchPriorityResolved('no-match');
    }
  }

  /**
   * Parse URL parameters into an object
   * @returns {Object} Key-value pairs of URL parameters
   */
  getURLParameters() {
    const params = {};
    const searchParams = new URLSearchParams(window.location.search);

    for (const [key, value] of searchParams.entries()) {
      params[key] = value.toLowerCase();
    }

    return params;
  }

  /**
   * Check if URL contains a matching UTM parameter
   * Dynamically checks all data-utm-* attributes on the modal element
   * @returns {boolean} True if there's a match
   */
  hasMatchingUTMParameter() {
    const urlParams = this.getURLParameters();
    const dataset = this.dataset;

    for (const key in dataset) {
      // Check if this is a UTM parameter (e.g., utmSource, utmMedium, utmCampaign)
      if (key.startsWith('utm')) {
        // Convert camelCase back to snake_case (utmSource -> utm_source)
        const paramName = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        const configuredValues = dataset[key];

        if (!configuredValues) continue;

        // Get the URL parameter value
        const urlParamValue = urlParams[paramName];
        if (!urlParamValue) continue;

        // Split configured values and check for match
        const valuesToMatch = configuredValues
          .split(',')
          .map(val => val.trim().toLowerCase())
          .filter(val => val.length > 0);

        if (valuesToMatch.includes(urlParamValue)) {
          return true; // Found a match!
        }
      }
    }

    return false; // No matches found
  }

  /**
   * Show the modal using native showModal()
   */
  show() {
    if (!this.dialog) return;

    // Use native showModal() - handles focus trap, backdrop, ESC key automatically
    this.dialog.showModal();

    // Remove focus from auto-focused element (prevents outline on open)
    // Focus will return when user presses Tab (keyboard navigation)
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }

    // Apply simple scroll lock using overflow hidden
    // Native dialog doesn't block background scroll on all mobile browsers
    document.body.style.overflow = 'hidden';

    // Pause any playing media
    if (window.pauseAllMedia) {
      window.pauseAllMedia();
    }
  }

  /**
   * Hide the modal and clean up
   */
  hide() {
    if (!this.dialog) return;

    // Use native close() method
    this.dialog.close();

    // Remove scroll lock
    document.body.style.overflow = '';

    // Pause any playing media
    if (window.pauseAllMedia) {
      window.pauseAllMedia();
    }

    // Dispatch custom event that other popups can listen for
    this.dispatchPriorityResolved('closed');
  }

  /**
   * Trigger the modal display and mark as shown
   */
  triggerModal() {
    try {
      this.show();
      sessionStorage.setItem(this.sessionKey, 'true');
    } catch (error) {
      console.error('UTM Modal: Failed to show modal', error);
    }
  }

  /**
   * Dispatch priority-modal-resolved event
   * @param {string} reason - Reason for resolution ('closed', 'no-match', 'already-shown')
   */
  dispatchPriorityResolved(reason) {
    const event = new CustomEvent('priority-modal-resolved', {
      bubbles: true,
      detail: { modalId: this.id, reason }
    });
    document.dispatchEvent(event);
  }
}

// Define the custom element
if (!customElements.get('utm-modal')) {
  customElements.define('utm-modal', UTMModal);
}
